import { useQuery } from "@tanstack/react-query"
import {
  getAirshipHangar,
  getBankPoints,
  getBoosterShopPoints,
  getBossPoints,
  getCardShopPoints,
  getCityHallPoint,
  getHotelPoints,
  getLocationPoints,
  getPostOfficePoints,
  getTreasurePoints,
  getWiseTreePoints,
  getWorldBossActive,
  getWarStatus,
} from "@/api/gameApi"
import { AcademicLeague } from "@/types/battle"
import { ForeignScansMeta } from "@/types/location"
import {
  bankPointToWorldMap,
  boosterShopPointToWorldMap,
  bossPointToWorldMap,
  cityHallPointToWorldMap,
  hangarPointToWorldMap,
  hotelPointToWorldMap,
  postOfficePointToWorldMap,
  scanPointToWorldMap,
  shopPointToWorldMap,
  treasurePointToWorldMap,
  worldBossEventToWorldMap,
  wiseTreePointToWorldMap,
  WorldMapPoint,
} from "@/utils/worldMapPoints"
import { CityMapItem } from "@/types/location"

export function useGuardiansWorldMapPoints() {
  return useQuery({
    queryKey: ["guardians-world-map-points"],
    queryFn: async (): Promise<{
      points: WorldMapPoint[]
      cities: CityMapItem[]
      foreignScans: ForeignScansMeta | null
      ownCityDominantLeague: AcademicLeague | null
    }> => {
      const [
        locationData,
        bossPoints,
        worldBossActive,
        shopPoints,
        boosterShops,
        mailPoints,
        bankPoints,
        cityHallPoint,
        hotelPoints,
        wiseTreePoints,
        hangarPoint,
        treasureData,
        war,
      ] = await Promise.all([
        getLocationPoints(),
        getBossPoints().catch(() => []),
        getWorldBossActive().catch(() => ({ event: null, reason: null })),
        getCardShopPoints().catch(() => []),
        getBoosterShopPoints().catch(() => []),
        getPostOfficePoints().catch(() => []),
        getBankPoints().catch(() => []),
        getCityHallPoint().catch(() => null),
        getHotelPoints().catch(() => []),
        getWiseTreePoints().catch(() => []),
        getAirshipHangar().catch(() => null),
        getTreasurePoints().catch(() => null),
        getWarStatus().catch(() => null),
      ])

      const cities = locationData.cities

      const treasurePoints = (treasureData?.points ?? [])
        .filter((point) => point.isActiveNow || point.nextStartsAt != null)
        .map((point) =>
          treasurePointToWorldMap(point, {
            claimedToday: treasureData?.claimedToday ?? false,
            coinAmount:
              point.coinAmount ?? treasureData?.settings?.coinAmount ?? null,
            dustAmount:
              point.dustAmount ?? treasureData?.starfall?.dustAmount ?? null,
          })
        )

      const worldBossPoints =
        worldBossActive.event != null
          ? [worldBossEventToWorldMap(worldBossActive.event)]
          : []

      const points: WorldMapPoint[] = [
        ...locationData.points.map(scanPointToWorldMap),
        ...bossPoints.map(bossPointToWorldMap),
        ...worldBossPoints,
        ...shopPoints.map(shopPointToWorldMap),
        ...boosterShops.map(boosterShopPointToWorldMap),
        ...mailPoints.map(postOfficePointToWorldMap),
        ...bankPoints.map(bankPointToWorldMap),
        ...(cityHallPoint ? [cityHallPointToWorldMap(cityHallPoint)] : []),
        ...hotelPoints.map(hotelPointToWorldMap),
        ...wiseTreePoints.map(wiseTreePointToWorldMap),
        ...(hangarPoint ? [hangarPointToWorldMap(hangarPoint)] : []),
        ...treasurePoints,
      ]

      return {
        points,
        cities,
        foreignScans: locationData.foreignScans ?? null,
        ownCityDominantLeague: war?.dominantLeague ?? null,
      }
    },
    staleTime: 60_000,
  })
}
